/**
 * Modern Resume Analysis Dashboard - Redesigned for Keyword Matching
 * A clear, actionable comparison of Resume vs Job Description
 * Updated: Sidebar-native design with Stylion brand colors
 */

import { t } from "@lingui/macro";
import {
  Brain,
  Check,
  CheckCircle,
  Clock,
  Lightbulb,
  Lightning,
  MagnifyingGlass,
  Plus,
  Target,
  TrendUp,
  Warning,
  X,
} from "@phosphor-icons/react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@stylingcv/ui";
import { motion } from "framer-motion";
import { memo, useMemo, useState } from "react";

import { Progress } from "./ui/progress";
import { useResumeRefinement } from "../hooks/smart-ai/use-resume-refinement";
import type { AdvancedAnalysisResult } from "../services/smart-ai/advanced-resume-analysis";

type ModernResumeAnalysisDashboardProps = {
  analysisData: AdvancedAnalysisResult | null;
  loading: boolean;
  clearAnalysis: () => void;
};

const ModernResumeAnalysisDashboardComponent = ({
  analysisData,
  loading,
  clearAnalysis,
}: ModernResumeAnalysisDashboardProps) => {
  const { isRefining, smartFix } = useResumeRefinement();
  const [showAllKeywords, setShowAllKeywords] = useState(false);

  // Extract data from the new schema
  const rawData = useMemo(() => analysisData?.rawATSData || {}, [analysisData]);
  
  const overallScore = rawData.overallScore ?? rawData.matchSummary?.matchPercentage ?? 0;
  const keywordMatches = rawData.keywordMatches || [];
  const matchSummary = rawData.matchSummary || { totalKeywords: 0, matched: 0, missing: 0 };
  const quickFixes = rawData.quickFixes || [];
  const formatting = rawData.formatting || {};

  // Separate matched and missing keywords
  const matchedKeywords = keywordMatches.filter((k: any) => k.foundInResume);
  const missingKeywords = keywordMatches.filter((k: any) => !k.foundInResume);

  // Loading State
  if (loading) {
    return (
      <div className="flex min-h-[300px] flex-col items-center justify-center gap-4 rounded-2xl bg-gradient-to-br from-[#16344c]/10 to-[#268599]/10 p-8 dark:from-[#16344c]/40 dark:to-[#268599]/20">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <Brain className="size-12 text-brand-teal" />
        </motion.div>
        <p className="font-medium text-gray-700 dark:text-gray-300">{t`Comparing your resume to the job...`}</p>
      </div>
    );
  }

  // Empty State
  if (!analysisData || Object.keys(rawData).length === 0) {
    return (
      <div className="flex min-h-[200px] flex-col items-center justify-center gap-4 rounded-2xl bg-gray-50 p-8 dark:bg-gray-800/50">
        <MagnifyingGlass className="size-10 text-gray-400" />
        <p className="text-gray-500">{t`No analysis data available`}</p>
      </div>
    );
  }

  // Get score label
  const getScoreLabel = (score: number) => {
    if (score >= 80) return t`Ready to apply`;
    if (score >= 60) return t`Good match with room to improve`;
    if (score >= 40) return t`Needs work on key signals`;
    return t`Major gaps to address`;
  };

  return (
    <div className="space-y-4">
      {/* Score Hero - Brand gradient */}
      <section className="overflow-hidden rounded-2xl bg-gradient-to-br from-[#16344c] via-[#268599] to-[#268599] p-5 text-white shadow-lg shadow-[#16344c]/20">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/70">
              {t`Keyword Match`}
            </div>
            <p className="mt-2 text-sm leading-relaxed text-white/90">
              {getScoreLabel(overallScore)}
            </p>
          </div>
          <div className="text-right">
            <div className="text-5xl font-black leading-none tracking-tight">
              {Math.round(overallScore)}%
            </div>
            <div className="mt-1 text-xs font-semibold text-white/80">
              {matchSummary.matched}/{matchSummary.totalKeywords} {t`found`}
            </div>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="mt-5">
          <div className="h-2 overflow-hidden rounded-full bg-white/20">
            <div 
              className="h-full rounded-full bg-white transition-all duration-500"
              style={{ width: `${overallScore}%` }}
            />
          </div>
          <div className="mt-2 flex justify-between text-[10px] font-bold uppercase tracking-wide text-white/60">
            <span>{t`Weak`}</span>
            <span>{t`Strong`}</span>
            <span>{t`Ready`}</span>
          </div>
        </div>
      </section>

      {/* Score Breakdown Tiles */}
      {rawData.scoreBreakdown && (
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800/50">
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
              {t`JD Match`}
            </div>
            <div className="mt-1 text-xl font-black text-blue-600 dark:text-blue-400">
              {Math.round(rawData.scoreBreakdown.jdMatch)}%
            </div>
            <div className="text-[11px] text-gray-400">60% {t`weight`}</div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800/50">
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
              {t`Market DNA`}
            </div>
            <div className="mt-1 text-xl font-black text-brand-teal">
              {Math.round(rawData.scoreBreakdown.marketRelevance)}%
            </div>
            <div className="text-[11px] text-gray-400">30% {t`weight`}</div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800/50">
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
              {t`ATS Ready`}
            </div>
            <div className="mt-1 text-xl font-black text-green-600 dark:text-green-400">
              {Math.round(rawData.scoreBreakdown.atsReadiness)}%
            </div>
            <div className="text-[11px] text-gray-400">10% {t`weight`}</div>
          </div>
        </div>
      )}

      {/* Priority Fixes - Show first if there are any */}
      {quickFixes.length > 0 && (
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/50">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-gray-700">
            <div>
              <div className="flex items-center gap-2 font-bold">
                <Lightning className="size-4 text-amber-500" weight="fill" />
                {t`Priority fixes`}
              </div>
              <p className="mt-0.5 text-xs text-gray-500">{t`Fix these before applying`}</p>
            </div>
            <Badge className="bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300">
              {quickFixes.length} {t`action${quickFixes.length > 1 ? 's' : ''}`}
            </Badge>
          </div>
          <div className="space-y-2 p-3">
            {quickFixes.map((fix: any, i: number) => (
              <div
                key={i}
                className="flex items-start gap-3 rounded-xl border border-red-100 bg-red-50/50 p-3 dark:border-red-900/30 dark:bg-red-900/10"
              >
                <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                  <Warning className="size-3.5" weight="bold" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-gray-900 dark:text-white">
                    {fix.action}
                  </div>
                  <p className="mt-0.5 text-xs leading-relaxed text-gray-600 dark:text-gray-400">
                    {fix.impact}
                  </p>
                </div>
                <Button
                  size="sm"
                  disabled={isRefining}
                  onClick={() => smartFix("High", fix.action)}
                  className="shrink-0 gap-1 bg-[#16344c] text-white hover:bg-[#1a3a4f]"
                >
                  {isRefining ? (
                    <Clock className="size-3 animate-spin" />
                  ) : (
                    <Plus className="size-3" />
                  )}
                  {t`Add`}
                </Button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* No fixes needed state */}
      {quickFixes.length === 0 && (
        <div className="flex items-center gap-3 rounded-xl border border-green-200 bg-green-50/50 p-4 dark:border-green-900/30 dark:bg-green-900/10">
          <CheckCircle className="size-6 text-green-600" weight="fill" />
          <div>
            <div className="font-semibold text-green-800 dark:text-green-200">{t`No critical fixes needed`}</div>
            <p className="text-xs text-green-600 dark:text-green-400">{t`Your resume covers the key requirements`}</p>
          </div>
        </div>
      )}

      {/* Keyword Comparison - Simplified list */}
      <section className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/50">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-gray-700">
          <div className="flex items-center gap-2 font-bold">
            <MagnifyingGlass className="size-4 text-brand-teal" />
            {t`Keyword comparison`}
          </div>
          <div className="flex gap-1.5">
            <Badge className="bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300">
              <CheckCircle className="mr-1 size-3" />
              {matchedKeywords.length} {t`found`}
            </Badge>
            {missingKeywords.length > 0 && (
              <Badge className="bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                <X className="mr-1 size-3" />
                {missingKeywords.length} {t`missing`}
              </Badge>
            )}
          </div>
        </div>
        
        <div className="max-h-[280px] divide-y divide-gray-100 overflow-y-auto dark:divide-gray-700">
          {/* Show missing first */}
          {missingKeywords.map((match: any, i: number) => (
            <div key={`missing-${i}`} className="flex items-center justify-between gap-3 bg-red-50/30 px-4 py-3 dark:bg-red-900/5">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-gray-900 dark:text-white">
                  {match.keyword}
                </div>
                <div className="mt-0.5 text-xs text-gray-500">
                  {match.category === "critical" 
                    ? t`Required` 
                    : match.category === "market-trending" 
                      ? t`Market DNA` 
                      : t`Nice-to-have`}
                </div>
              </div>
              <Badge className="shrink-0 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                <X className="mr-1 size-3" />
                {t`Missing`}
              </Badge>
            </div>
          ))}
          
          {/* Show matched - limited unless expanded */}
          {(showAllKeywords ? matchedKeywords : matchedKeywords.slice(0, 4)).map((match: any, i: number) => (
            <div key={`matched-${i}`} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-gray-900 dark:text-white">
                  {match.keyword}
                </div>
                <div className="mt-0.5 text-xs text-gray-500">
                  {match.locationInResume || (
                    match.category === "critical" 
                      ? t`Required` 
                      : match.category === "market-trending" 
                        ? t`Market DNA` 
                        : t`Nice-to-have`
                  )}
                </div>
              </div>
              <Badge className="shrink-0 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                <CheckCircle className="mr-1 size-3" />
                {t`Found`}
              </Badge>
            </div>
          ))}
          
          {keywordMatches.length === 0 && (
            <div className="py-8 text-center text-gray-500">
              {t`No keyword data available`}
            </div>
          )}
        </div>
        
        {/* Show more/less toggle */}
        {matchedKeywords.length > 4 && (
          <button
            onClick={() => setShowAllKeywords(!showAllKeywords)}
            className="w-full border-t border-gray-100 px-4 py-2.5 text-xs font-semibold text-brand-teal hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
          >
            {showAllKeywords 
              ? t`Show less` 
              : t`Show all ${matchedKeywords.length} matched keywords`}
          </button>
        )}
      </section>

      {/* Market DNA Insights - Collapsible */}
      {(rawData.marketDNA?.role_summary || rawData.marketDNA?.top_hard_skills?.length > 0) && (
        <details className="group overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/50">
          <summary className="flex cursor-pointer items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800">
            <div className="flex items-center gap-2 font-bold">
              <Target className="size-4 text-brand-teal" weight="fill" />
              {t`Market DNA insights`}
            </div>
            <TrendUp className="size-4 text-gray-400 transition-transform group-open:rotate-180" />
          </summary>
          <div className="space-y-3 border-t border-gray-100 p-4 dark:border-gray-700">
            {rawData.marketDNA?.role_summary && (
              <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                {rawData.marketDNA.role_summary}
              </p>
            )}
            {(rawData.marketDNA?.top_hard_skills || rawData.marketDNA?.tech_stack)?.length > 0 && (
              <div>
                <div className="mb-2 text-xs font-bold uppercase tracking-wider text-gray-500">
                  {t`Trending skills`}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(rawData.marketDNA?.top_hard_skills || rawData.marketDNA?.tech_stack || []).slice(0, 8).map((skill: string) => (
                    <Badge
                      key={skill}
                      variant="secondary"
                      className="border border-brand-teal/20 bg-brand-teal/10 text-xs text-brand-teal"
                    >
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {rawData.marketDNA?.interview_focus && (
              <div className="rounded-lg bg-amber-50 p-3 dark:bg-amber-900/10">
                <div className="flex items-start gap-2">
                  <Lightbulb className="mt-0.5 size-4 shrink-0 text-amber-600" weight="fill" />
                  <p className="text-xs leading-relaxed text-amber-800 dark:text-amber-200">
                    {rawData.marketDNA.interview_focus}
                  </p>
                </div>
              </div>
            )}
          </div>
        </details>
      )}

      {/* ATS Readiness Checklist - Collapsible */}
      <details className="group overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/50">
        <summary className="flex cursor-pointer items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800">
          <div className="flex items-center gap-2 font-bold">
            <Check className="size-4 text-green-600" weight="bold" />
            {t`ATS readiness`}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">
              {[formatting.hasContactInfo, formatting.hasExperienceSection, formatting.hasSkillsSection, formatting.hasEducationSection].filter(Boolean).length}/4
            </span>
            <TrendUp className="size-4 text-gray-400 transition-transform group-open:rotate-180" />
          </div>
        </summary>
        <div className="grid grid-cols-2 gap-2 border-t border-gray-100 p-3 dark:border-gray-700">
          {[
            { key: "hasContactInfo", label: t`Contact Info`, value: formatting.hasContactInfo },
            { key: "hasExperienceSection", label: t`Experience`, value: formatting.hasExperienceSection },
            { key: "hasSkillsSection", label: t`Skills`, value: formatting.hasSkillsSection },
            { key: "hasEducationSection", label: t`Education`, value: formatting.hasEducationSection },
          ].map((check) => (
            <div
              key={check.key}
              className={`flex items-center gap-2 rounded-lg p-2.5 text-sm ${
                check.value
                  ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300"
                  : check.value === false
                    ? "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300"
                    : "bg-gray-50 text-gray-500 dark:bg-gray-800/50"
              }`}
            >
              {check.value ? (
                <CheckCircle className="size-4" weight="fill" />
              ) : check.value === false ? (
                <Warning className="size-4" />
              ) : (
                <div className="size-4" />
              )}
              <span className="text-xs font-medium">{check.label}</span>
            </div>
          ))}
        </div>
      </details>

      {/* Raw Data - Collapsed by default */}
      <details className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
        <summary className="cursor-pointer text-xs font-medium text-gray-500">
          {t`View raw analysis data`}
        </summary>
        <pre className="mt-3 max-h-48 overflow-auto rounded-lg bg-gray-900 p-3 text-[10px] leading-relaxed text-gray-100">
          {JSON.stringify(rawData, null, 2)}
        </pre>
      </details>

      {/* New Analysis Button */}
      <Button variant="outline" className="w-full" onClick={clearAnalysis}>
        {t`Start new analysis`}
      </Button>
    </div>
  );
};

export const ModernResumeAnalysisDashboard = memo(ModernResumeAnalysisDashboardComponent);
